/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-33 / G-64 — the TinyPrint receipt printer, over Bluetooth Low Energy
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHY A THIRD-PARTY TRANSPORT HERE, WHEN THE NFC PLUGIN IS OURS. The fob reader
 * sits in the AUTHENTICATION path and hands the app a credential, so a
 * dependency there is a risk and no free option exposed what G-48 needed. A
 * printer is not in that path: it receives a picture of a receipt. So the
 * generic, maintained, MIT Bluetooth plugin does the boring half (connect,
 * negotiate, write) and ALL the printer knowledge stays in our TypeScript,
 * where `receiptRender.ts` already builds and tests the bytes. Owner agreed
 * 2026-09-11.
 *
 * NOTHING IS HARDCODED ABOUT A PARTICULAR PRINTER. The owner has several
 * TinyPrints that are "slightly different", and the research recorded in G-33
 * is explicit: the raster command is stable across this family, the GATT UUIDs
 * are NOT. So pairing DISCOVERS the writable characteristic on the actual
 * device and remembers it. Hardcoding the reference's `0000ff02` pair is how
 * you get two printers that work and a third that connects and silently does
 * nothing.
 */

import { t } from "../i18n/t";
import { isNativeShell } from "./shell";

/**
 * G-65: THIS MODULE NO LONGER KNOWS WHERE PRINTERS ARE REMEMBERED.
 *
 * A counter now has TWO of them — one with receipt paper, one with sticker
 * paper for gift notes — so "the paired printer" stopped being a thing a
 * transport could resolve on its own. Which device a job goes to is a question
 * about the SHOP's setup, and it lives in `printerRegistry.ts`; this file
 * answers only "given this device, put these bytes on paper". That also makes
 * the transport testable with a fake device instead of a fake localStorage.
 */

export interface PairedPrinter {
  deviceId: string;
  name: string | null;
  serviceUuid: string;
  characteristicUuid: string;
  /** Whether the chosen characteristic supports the faster unacknowledged write. */
  writeWithoutResponse: boolean;
}

interface BleCharacteristicLike {
  uuid: string;
  properties: { write: boolean; writeWithoutResponse: boolean; notify?: boolean };
}
interface BleServiceLike {
  uuid: string;
  characteristics: BleCharacteristicLike[];
}

/**
 * Imported lazily so a plain browser never pays for a native-only dependency,
 * and so the module cannot throw at import time where Bluetooth does not exist.
 */
async function ble() {
  const mod = await import("@capacitor-community/bluetooth-le");
  return mod.BleClient;
}

/**
 * Nothing Bluetooth may hang forever.
 *
 * Found on the real printer 2026-09-11: `connect` to a device that is already
 * held by ANOTHER app (these printers accept one connection at a time, and the
 * vendor's phone app takes it) never resolves AND never rejects — the plugin's
 * own `timeout` option did not fire. Without this the florist watches
 * "Printing…" forever and is told nothing, which is worse than a failure.
 */
async function withTimeout<T>(
  work: Promise<T>,
  ms: number,
  message: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Long enough for a sleepy printer to wake, short enough to not feel broken.
 *
 * Passed to the PLUGIN as well as used by our own race. Found on the owner's
 * tablet 2026-09-12: the plugin has a 10 s default of its own, so it always
 * rejected first with a bare "Connection timeout." and the useful sentence
 * below was never once shown to anybody.
 */
const CONNECT_TIMEOUT_MS = 12_000;
/** A full receipt is tens of kilobytes at BLE speed; this is a stall, not slowness. */
const PRINT_TIMEOUT_MS = 60_000;

/**
 * How fast we are willing to push bytes at a printer that has not asked us to
 * stop. Deliberately well under what the head can burn: going too fast costs a
 * receipt that ends halfway down with no error at all, and going too slow costs
 * a second. When the printer sends flow control (see below) that takes over and
 * this is only the floor.
 */
const SAFE_BYTES_PER_SEC = 5_000;

/**
 * The printer's own back-pressure, which it sends unprompted on a notify
 * characteristic: `51 78 ae 01 01 00 10 …` means its buffer is filling and we
 * must stop; the same packet with `00` means carry on. Honouring it is what
 * lets a long ticket print in full — the alternative is guessing a rate that
 * works for a short receipt and truncates a long one.
 */
const FLOW_CONTROL_COMMAND = 0xae;
const FLOW_PAUSE_BIT = 0x10;
/** If a pause is never lifted, fail loudly rather than hang on "Printing…". */
const MAX_PAUSE_MS = 10_000;

/**
 * G-85 S4: a FUNCTION, not a constant. A module-level constant holding
 * user-facing text is evaluated once at import — in English, always, whatever
 * the tablet is set to. This is the third time that trap has been found here
 * (`FOB_ANTENNA_HINT` and the two `ShopModeContext` session messages were the
 * first two), so the rule is: compute the words where they are thrown.
 */
const busyHint = () => t("printer.couldNotReach");

/** How the plugin and our own race each spell a connect that ran out of time. */
const CONNECT_TIMEOUT_PATTERN = /timeout|timed out/i;

/**
 * Services we ask to be granted access to, WITHOUT filtering the picker on
 * them.
 *
 * `ae30` is the cat-printer family's service, discovered on the owner's real
 * X5 during G-64 and recorded in `receipt/catPrinter.ts`. Listing it here is
 * what makes a browser hand us the characteristic after connecting.
 *
 * It is deliberately NOT a `services` filter. A filter matches what a device
 * ADVERTISES, and a printer that keeps `ae30` back until you connect would
 * then be missing from the picker entirely — a worse failure than a long
 * list, because nothing on screen would explain it. The generic serial
 * services below are the same family's alternates, seen on relabelled units.
 * If a device turns out to advertise `ae30` reliably, filtering becomes a
 * safe follow-up with evidence behind it.
 */
const KNOWN_PRINTER_SERVICES = [
  "0000ae30-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
];

/**
 * Which services to ask a browser for — the four guesses above PLUS every
 * service this tablet has actually printed through before.
 *
 * The guesses alone are not a general answer, and pretending otherwise is how
 * the original bug read: a relabelled unit whose writable characteristic sits
 * on a vendor UUID we did not guess still finds nothing, and the florist gets
 * told their working printer is not a printer. But a printer that has ever
 * been paired — in the native shell, where Android shows every service —
 * leaves its real `serviceUuid` in the registry. Feeding those back means the
 * browser learns from the app instead of guessing again.
 *
 * Lower-cased because Web Bluetooth compares UUIDs as lower-case strings and
 * a stored upper-case one would silently never match.
 */
export function servicesToRequest(alreadyPaired: readonly string[]): string[] {
  const all = [...KNOWN_PRINTER_SERVICES, ...alreadyPaired]
    .filter((uuid) => typeof uuid === "string" && uuid.length > 0)
    .map((uuid) => uuid.toLowerCase());
  return [...new Set(all)];
}

/**
 * What to SHOW when a connect fails. Pure, so the rule is testable without a
 * Bluetooth stack.
 *
 * ONLY A TIMEOUT IS REWRITTEN, and that is the whole point of this function.
 * A timeout is the one failure whose own wording ("Connection timeout.") tells
 * the person at the counter nothing, while its two real causes — another device
 * holding the printer, or a printer asleep — are both things they can fix in
 * five seconds. So a timeout gains the hint and keeps its original text in
 * brackets for whoever is debugging.
 *
 * EVERYTHING ELSE IS RETURNED UNCHANGED. "Bluetooth is not enabled", a denied
 * location permission (Android requires one to scan for BLE devices), "device
 * not found" — each is already the most useful sentence available, and the
 * first draft of this replaced all of them. `/code-review` 2026-09-12 caught
 * it: a florist with Bluetooth switched off was told to press the printer's
 * button and disconnect it from a phone, with the real cause buried in
 * brackets at the end of three sentences on a 300 px screen. Asserting a cause
 * we have not established is worse than repeating the stack's own words.
 */
export function connectFailureMessage(detail: string): string {
  // `withTimeout`'s own race already rejects with the hint, and a wrapped
  // message still contains the word "timeout" — so match on the prefix rather
  // than equality, and the function stays idempotent either way.
  const hint = busyHint();
  if (detail.startsWith(hint)) return detail;
  return CONNECT_TIMEOUT_PATTERN.test(detail)
    ? `${hint} (${detail})`
    : detail;
}

/** Connects, and makes sure a TIMEOUT says something a florist can act on. */
async function connectWithHint(
  BleClient: {
    connect: (
      id: string,
      cb: () => void,
      opts?: { timeout?: number },
    ) => Promise<void>;
  },
  deviceId: string,
): Promise<void> {
  try {
    await withTimeout(
      BleClient.connect(deviceId, () => undefined, {
        timeout: CONNECT_TIMEOUT_MS,
      }),
      CONNECT_TIMEOUT_MS + 1_000,
      busyHint(),
    );
  } catch (err: unknown) {
    const detail = err instanceof Error ? err.message : String(err);
    const message = connectFailureMessage(detail);
    // When the stack's own words survive, rethrow the original error rather
    // than a copy of it: the stack trace and anything the plugin attached are
    // what a bug report needs.
    if (message === detail && err instanceof Error) throw err;
    throw new Error(message);
  }
}

export function isPrinterSupported(): boolean {
  return isNativeShell();
}

/**
 * Asks the person to pick a device, then works out how to talk to it.
 *
 * DISCOVERY ONLY — it does not remember anything. The caller decides which
 * ROLE the chosen device fills and stores it (`printerRegistry.ts`), because
 * only a human can say whether the machine they just picked is the one holding
 * receipt paper or the one holding stickers. Guessing from the device name
 * would be worse than asking: G-33 records that the owner's TinyPrints are
 * "slightly different" and their names collide.
 *
 * `requestDevice` shows Android's own picker rather than us building a scan
 * list — it is the system UI a person already recognises, and it keeps us out
 * of the business of ranking signal strength. It still lists EVERYTHING
 * nearby: see `KNOWN_PRINTER_SERVICES` for why we grant a service without
 * filtering on it.
 */
export async function discoverPrinter(
  alreadyPairedServices: readonly string[] = [],
): Promise<PairedPrinter> {
  const BleClient = await ble();
  await BleClient.initialize();

  const device = await BleClient.requestDevice({
    // Web Bluetooth grants a page access to NO GATT service unless it was
    // named in `services` or `optionalServices` — the plugin says so in its
    // own docs. We named neither, so in a BROWSER every pairing got as far as
    // connecting and then found nothing writable, and the florist read "That
    // device has nothing to print to — is it the printer?" about the actual
    // printer. It went unnoticed because the shop tablet runs the native
    // shell, where Android's stack has no such allowlist: this path was only
    // ever broken in Chrome, which is exactly where it is tested.
    optionalServices: servicesToRequest(alreadyPairedServices),
    allowDuplicates: false,
  });
  await connectWithHint(BleClient, device.deviceId);

  try {
    const services = await BleClient.getServices(device.deviceId);
    const found = pickWritableCharacteristic(
      services as unknown as BleServiceLike[],
    );
    if (!found) {
      // TWO DIFFERENT FAILURES, and conflating them is what sent this session
      // hunting hardware for an hour. An EMPTY list means the browser granted
      // us nothing — the device may well be the printer, we simply cannot see
      // its channel. A non-empty list with nothing writable means the thing
      // really does not take print jobs. Say which.
      throw new Error(
        services.length === 0
          ? t("printer.noServiceGranted")
          : t("printer.nothingToPrintTo"),
      );
    }
    return {
      deviceId: device.deviceId,
      name: device.name ?? null,
      ...found,
    };
  } finally {
    await BleClient.disconnect(device.deviceId).catch(() => undefined);
  }
}

/**
 * Finds the characteristic a receipt can be written to.
 *
 * Exported and pure so the choosing RULE is tested without a radio. Prefers
 * unacknowledged writes, which these printers are built around and which are
 * several times faster over BLE; falls back to acknowledged. Vendor services
 * (the 16-bit `0000xxxx` shorthand these printers use) are preferred over the
 * standard GATT services every device carries, so we do not try to print to
 * the battery-level characteristic.
 */
export function pickWritableCharacteristic(
  services: BleServiceLike[],
): { serviceUuid: string; characteristicUuid: string; writeWithoutResponse: boolean } | null {
  const candidates: Array<{
    serviceUuid: string;
    characteristicUuid: string;
    writeWithoutResponse: boolean;
    score: number;
  }> = [];

  for (const service of services ?? []) {
    // 0x1800/0x1801 generic access & attribute, 0x180a device info, 0x180f
    // battery — present on nearly everything and never where a receipt goes.
    const standard = /^0000(1800|1801|180a|180f)-/i.test(service.uuid);
    for (const ch of service.characteristics ?? []) {
      const canWriteFast = ch.properties?.writeWithoutResponse === true;
      const canWrite = ch.properties?.write === true;
      if (!canWriteFast && !canWrite) continue;
      candidates.push({
        serviceUuid: service.uuid,
        characteristicUuid: ch.uuid,
        writeWithoutResponse: canWriteFast,
        score: (standard ? 0 : 2) + (canWriteFast ? 1 : 0),
      });
    }
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.score - a.score);
  const { serviceUuid, characteristicUuid, writeWithoutResponse } = candidates[0];
  return { serviceUuid, characteristicUuid, writeWithoutResponse };
}

/**
 * Splits a raster into packets the radio will actually carry.
 *
 * Pure, and tested, because getting it wrong is invisible in code and obvious
 * on paper. A BLE packet holds the negotiated MTU minus three bytes of ATT
 * header; anything larger is silently truncated by the stack, which prints
 * half a ticket and reports success.
 */
export function chunkForMtu(bytes: Uint8Array, mtu: number): Uint8Array[] {
  const payload = Math.max(20, Math.min(512, mtu - 3));
  const out: Uint8Array[] = [];
  for (let i = 0; i < bytes.length; i += payload) {
    out.push(bytes.subarray(i, Math.min(i + payload, bytes.length)));
  }
  return out;
}

/**
 * Reads the printer's flow-control packets out of a notification.
 *
 * Returns null for anything else it says — the printer also reports battery and
 * temperature on the same characteristic, and misreading one of those as "stop"
 * would stall a perfectly good print.
 */
export function readFlowControl(bytes: Uint8Array): "pause" | "resume" | null {
  for (let i = 0; i + 7 < bytes.length; i += 1) {
    if (bytes[i] !== 0x51 || bytes[i + 1] !== 0x78) continue;
    if (bytes[i + 2] !== FLOW_CONTROL_COMMAND) continue;
    return bytes[i + 6] & FLOW_PAUSE_BIT ? "pause" : "resume";
  }
  return null;
}

/**
 * Sends a prepared print job to the paired printer.
 *
 * PACING, not speed. These printers have a small buffer, and pushing packets as
 * fast as the radio accepts them produces a ticket that stops part-way rather
 * than an error. Two things hold it back: a rate we choose, well below what the
 * head can burn, and the printer's own pause/resume packets when it sends them.
 * A receipt is not a thing worth racing to produce.
 */
/**
 * Where a print actually spent its time.
 *
 * Returned rather than logged because the three levers on print speed — the
 * rate cap, the head speed, and reconnecting every time — cost wildly
 * different amounts and NOTHING so far has measured which dominates. Tuning
 * all three blind is how you end up with a printer that is no quicker and a
 * pile of changes nobody can attribute.
 */
export interface PrintStats {
  bytes: number;
  chunks: number;
  /** Reaching the printer: initialize, connect, discover, subscribe. */
  connectMs: number;
  /** Pushing the bytes, including every deliberate gap. */
  transferMs: number;
  /** How much of `transferMs` was us waiting on our own rate cap. */
  pacingMs: number;
  /** Whether the printer's own back-pressure channel was found at all. */
  flowControl: boolean;
  /** How many times it actually asked us to wait. */
  pauses: number;
  /** Total time blocked on those pauses. */
  pausedMs: number;
}

export async function printRaster(
  printer: PairedPrinter,
  bytes: Uint8Array,
  options: { bytesPerSecond?: number } = {},
): Promise<PrintStats> {
  const startedAt = Date.now();
  const BleClient = await ble();
  await BleClient.initialize();
  await connectWithHint(BleClient, printer.deviceId);

  let paused = false;
  let pauses = 0;
  let pausedMs = 0;
  let pacingMs = 0;
  let notifying: { service: string; characteristic: string } | null = null;

  try {
    let mtu = 23; // the BLE floor; anything better is a bonus
    try {
      const negotiated = await BleClient.getMtu(printer.deviceId);
      if (Number.isFinite(negotiated) && negotiated > 0) mtu = negotiated;
    } catch {
      // Not every stack reports it. The floor still prints, just slower.
    }

    // Listen for back-pressure if the printer offers it. Best effort on
    // purpose: a printer that never notifies still prints at the paced rate,
    // so a failure here must not fail the receipt.
    try {
      const services = (await BleClient.getServices(
        printer.deviceId,
      )) as unknown as BleServiceLike[];
      const service = (services ?? []).find((s) => s.uuid === printer.serviceUuid);
      const channel = (service?.characteristics ?? []).find(
        (c) => c.properties?.notify === true,
      );
      if (service && channel) {
        await BleClient.startNotifications(
          printer.deviceId,
          service.uuid,
          channel.uuid,
          (value: DataView) => {
            const signal = readFlowControl(
              new Uint8Array(value.buffer, value.byteOffset, value.byteLength),
            );
            if (!signal) return;
            if (signal === "pause" && !paused) pauses += 1;
            paused = signal === "pause";
          },
        );
        notifying = { service: service.uuid, characteristic: channel.uuid };
      }
    } catch {
      // No notifications. The rate limit below is then the only back-pressure.
    }

    const rate = options.bytesPerSecond ?? SAFE_BYTES_PER_SEC;
    const deadline = Date.now() + PRINT_TIMEOUT_MS;
    const packets = chunkForMtu(bytes, mtu);
    const connectMs = Date.now() - startedAt;
    const transferStartedAt = Date.now();

    for (const packet of packets) {
      // Wait out a pause before spending the next packet, not after.
      const pausedUntil = Date.now() + MAX_PAUSE_MS;
      const pauseStartedAt = Date.now();
      while (paused) {
        if (Date.now() > pausedUntil) {
          throw new Error(
            t("printer.neverResumed"),
          );
        }
        await new Promise((r) => setTimeout(r, 50));
      }
      pausedMs += Date.now() - pauseStartedAt;
      if (Date.now() > deadline) {
        throw new Error(
          t("printer.stoppedPartWay"),
        );
      }

      const view = new DataView(
        packet.buffer.slice(packet.byteOffset, packet.byteOffset + packet.byteLength),
      );
      if (printer.writeWithoutResponse) {
        await BleClient.writeWithoutResponse(
          printer.deviceId,
          printer.serviceUuid,
          printer.characteristicUuid,
          view,
        );
      } else {
        await BleClient.write(
          printer.deviceId,
          printer.serviceUuid,
          printer.characteristicUuid,
          view,
        );
      }
      // An unacknowledged write returns before the printer has consumed
      // anything, so this gap is most of the back-pressure there is.
      const gap = (packet.length / rate) * 1000;
      pacingMs += gap;
      await new Promise((r) => setTimeout(r, gap));
    }

    return {
      bytes: bytes.length,
      chunks: packets.length,
      connectMs,
      transferMs: Date.now() - transferStartedAt,
      pacingMs: Math.round(pacingMs),
      flowControl: notifying !== null,
      pauses,
      pausedMs,
    };
  } finally {
    if (notifying) {
      await BleClient.stopNotifications(
        printer.deviceId,
        notifying.service,
        notifying.characteristic,
      ).catch(() => undefined);
    }
    await BleClient.disconnect(printer.deviceId).catch(() => undefined);
  }
}
